import React, { useEffect, useMemo, useState } from 'react'
import Table from 'react-bootstrap/Table'
import Modal from 'react-bootstrap/Modal'
import Spinner from 'react-bootstrap/Spinner'
import Form from 'react-bootstrap/Form'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMagnifyingGlass, faPlus, faArrowUpRightFromSquare, faImages, faCircleCheck, faClock, faCircleExclamation } from '@fortawesome/free-solid-svg-icons'
import { useNavigate } from 'react-router-dom'
import { collection, getDocs, orderBy, query, where } from 'firebase/firestore'
import { db } from '../firebase'
import MaskedPhone from '../Components/MaskedPhone';

const getStatus = (approved, declined, total) => {
  if (declined > 0) return 'Declined'
  if (total > 0 && approved === total) return 'Approved'
  return 'Pending'
}

const getRequestCounts = (request, statsByRequest) => {
  const stats = statsByRequest[request.id] || { approved: 0, declined: 0, total: 0 }
  const total = request.studentCount || (request.studentIds ? request.studentIds.length : 0) || stats.total
  const approved = stats.approved || 0
  const declined = stats.declined || 0
  return { total, approved, declined, pending: Math.max(0, total - approved - declined) }
}

function MediaConsentComp() {
  const navigate = useNavigate()
  const [requests, setRequests] = useState([])
  const [statsByRequest, setStatsByRequest] = useState({})
  const [loading, setLoading] = useState(true)
  const [modalShow, setModalShow] = useState(false)
  const [modalLoading, setModalLoading] = useState(false)
  const [modalStudents, setModalStudents] = useState([])
  const [modalStatus, setModalStatus] = useState('approved')
  const [modalRequestTitle, setModalRequestTitle] = useState('')
  const [mediaModalShow, setMediaModalShow] = useState(false)
  const [mediaModalRequest, setMediaModalRequest] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedStatus, setSelectedStatus] = useState('all')

  useEffect(() => {
    const fetchRequests = async () => {
      try {
        const q = query(collection(db, 'MediaConsentRequests'), orderBy('createdAt', 'desc'))
        const snapshot = await getDocs(q)
        const items = []
        snapshot.forEach((doc) => {
          items.push({ id: doc.id, ...doc.data() })
        })
        setRequests(items)
      } catch (err) {
        console.error('Failed to load media consent requests', err)
      } finally {
        setLoading(false)
      }
    }

    fetchRequests()
  }, [])

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'MediaConsentTokens'))
        const map = {}
        snapshot.forEach((doc) => {
          const data = doc.data()
          const requestId = data.requestId
          if (!requestId) return
          if (!map[requestId]) {
            map[requestId] = { approved: 0, declined: 0, total: 0 }
          }
          map[requestId].total += 1
          if (data.status === 'approved') {
            map[requestId].approved += 1
          } else if (data.status === 'declined') {
            map[requestId].declined += 1
          }
        })
        setStatsByRequest(map)
      } catch (err) {
        console.error('Failed to load media consent stats', err)
      }
    }

    fetchStats()
  }, [])

  const getStatusClass = (status) => {
    if (status === 'Approved') return 'mediaConsentBadge--approved'
    if (status === 'Declined') return 'mediaConsentBadge--declined'
    return 'mediaConsentBadge--pending'
  }

  const requestSummary = useMemo(() => requests.reduce((summary, request) => {
    const counts = getRequestCounts(request, statsByRequest)
    const status = getStatus(counts.approved, counts.declined, counts.total)
    summary.total += 1
    summary.pending += status === 'Pending' ? 1 : 0
    summary.approved += counts.approved
    summary.declined += counts.declined
    return summary
  }, { total: 0, pending: 0, approved: 0, declined: 0 }), [requests, statsByRequest])

  const visibleRequests = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase()
    return requests.filter((request) => {
      const counts = getRequestCounts(request, statsByRequest)
      const status = getStatus(counts.approved, counts.declined, counts.total).toLowerCase()
      const matchesSearch = !normalizedSearch
        || `${request.title || ''} ${request.description || ''}`.toLowerCase().includes(normalizedSearch)
      const matchesStatus = selectedStatus === 'all' || status === selectedStatus
      return matchesSearch && matchesStatus
    })
  }, [requests, searchTerm, selectedStatus, statsByRequest])

  const getVideoEmbedUrl = (url) => {
    try {
      const parsed = new URL(url)
      const host = parsed.hostname.replace('www.', '')
      if (host === 'youtu.be') {
        const videoId = parsed.pathname.slice(1)
        return videoId ? `https://www.youtube.com/embed/${videoId}` : null
      }
      if (host.includes('youtube.com')) {
        const videoId = parsed.searchParams.get('v')
        return videoId ? `https://www.youtube.com/embed/${videoId}` : null
      }
      if (host.includes('vimeo.com')) {
        const parts = parsed.pathname.split('/').filter(Boolean)
        const videoId = parts[parts.length - 1]
        return videoId ? `https://player.vimeo.com/video/${videoId}` : null
      }
    } catch (err) {
      return null
    }
    return null
  }

  const isDirectVideo = (url) => {
    return /\.(mp4|webm|ogg)(\?|#|$)/i.test(url)
  }

  const openMediaModal = (request) => {
    setMediaModalRequest(request)
    setMediaModalShow(true)
  }

  const renderMediaItems = (request) => {
    if (!request || !request.media || request.media.length === 0) {
      return <div className="text-center">No media attached.</div>
    }

    return (
      <div className="media-consent-media">
        {request.media.map((item) => (
          <div key={item.url} className="media-consent-media-item">
            {item.type && item.type.startsWith('image') && <img src={item.url} alt={item.name || 'Media'} />}
            {item.type && item.type.startsWith('video') && (() => {
              const embedUrl = getVideoEmbedUrl(item.url)
              if (embedUrl) {
                return (
                  <iframe
                    src={embedUrl}
                    title={item.name || 'Video'}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                )
              }
              if (isDirectVideo(item.url)) {
                return <video controls src={item.url} />
              }
              return (
                <a href={item.url} target="_blank" rel="noopener noreferrer" className="btn btn-outline-primary btn-sm">
                  Open Video
                </a>
              )
            })()}
          </div>
        ))}
      </div>
    )
  }

  const openModal = async (request, status) => {
    setModalShow(true)
    setModalLoading(true)
    setModalStudents([])
    setModalStatus(status)
    setModalRequestTitle(request.title || '')

    try {
      const q = query(
        collection(db, 'MediaConsentTokens'),
        where('requestId', '==', request.id),
        where('status', '==', status)
      )
      const snapshot = await getDocs(q)
      const items = []
      snapshot.forEach((docSnap) => {
        items.push({ id: docSnap.id, ...docSnap.data() })
      })
      setModalStudents(items)
    } catch (err) {
      console.error('Failed to load consent responses', err)
    } finally {
      setModalLoading(false)
    }
  }

  return (
    <div className="clientsComp mediaConsentPage">
      <header className="mediaConsentHeader">
        <div>
          <p className="mediaConsentEyebrow">COMMUNICATIONS</p>
          <h1>Media consent</h1>
          <p>Manage requests and keep track of student responses.</p>
        </div>
        <button className="mediaConsentPrimaryButton" onClick={() => navigate('/media-consent/new')}>
          <FontAwesomeIcon icon={faPlus} />
          New request
        </button>
      </header>

      <section className="mediaConsentOverview" aria-label="Consent overview">
        <div className="mediaConsentMetric">
          <span className="mediaConsentMetricIcon"><FontAwesomeIcon icon={faImages} /></span>
          <div><span>Total requests</span><strong>{loading ? '—' : requestSummary.total}</strong></div>
        </div>
        <div className="mediaConsentMetric">
          <span className="mediaConsentMetricIcon mediaConsentMetricIcon--pending"><FontAwesomeIcon icon={faClock} /></span>
          <div><span>Awaiting responses</span><strong>{loading ? '—' : requestSummary.pending}</strong></div>
        </div>
        <div className="mediaConsentMetric">
          <span className="mediaConsentMetricIcon mediaConsentMetricIcon--approved"><FontAwesomeIcon icon={faCircleCheck} /></span>
          <div><span>Approved responses</span><strong>{loading ? '—' : requestSummary.approved}</strong></div>
        </div>
        <div className="mediaConsentMetric">
          <span className="mediaConsentMetricIcon mediaConsentMetricIcon--declined"><FontAwesomeIcon icon={faCircleExclamation} /></span>
          <div><span>Declined responses</span><strong>{loading ? '—' : requestSummary.declined}</strong></div>
        </div>
      </section>

      <section className="mediaConsentListCard">
        <div className="mediaConsentListHeader">
          <div>
            <h2>Consent requests</h2>
            <p>{loading ? 'Loading requests…' : `${visibleRequests.length} of ${requests.length} requests`}</p>
          </div>
          <div className="mediaConsentFilters">
            <div className="mediaConsentSearch">
              <FontAwesomeIcon icon={faMagnifyingGlass} aria-hidden="true" />
              <Form.Control
                type="search"
                aria-label="Search consent requests"
                placeholder="Search requests"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </div>
            <Form.Select
              aria-label="Filter requests by status"
              value={selectedStatus}
              onChange={(event) => setSelectedStatus(event.target.value)}
            >
              <option value="all">All statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="declined">Declined</option>
            </Form.Select>
          </div>
        </div>

        {loading ? (
          <div className="mediaConsentLoading" role="status">
            <Spinner animation="border" size="sm" />
            <span>Loading consent requests</span>
          </div>
        ) : requests.length === 0 ? (
          <div className="mediaConsentEmptyState">
            <span className="mediaConsentEmptyIcon"><FontAwesomeIcon icon={faImages} /></span>
            <h3>No consent requests yet</h3>
            <p>Create your first request to share media and collect student consent.</p>
            <button className="mediaConsentPrimaryButton" onClick={() => navigate('/media-consent/new')}>
              <FontAwesomeIcon icon={faPlus} />
              Create a request
            </button>
          </div>
        ) : visibleRequests.length === 0 ? (
          <div className="mediaConsentEmptyState mediaConsentEmptyState--compact">
            <h3>No matching requests</h3>
            <p>Try another search or status filter.</p>
            <button type="button" className="mediaConsentResetButton" onClick={() => { setSearchTerm(''); setSelectedStatus('all') }}>
              Clear filters
            </button>
          </div>
        ) : (
          <div className="table-responsive mediaConsentTableWrap">
            <Table className="mediaConsentTable" hover>
              <thead>
                <tr>
                  <th>Request</th>
                  <th>Created</th>
                  <th>Responses</th>
                  <th>Progress</th>
                  <th>Media</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {visibleRequests.map((request) => {
                  const { total, approved, declined, pending } = getRequestCounts(request, statsByRequest)
                  const status = getStatus(approved, declined, total)
                  const createdAt = request.createdAt?.toDate ? request.createdAt.toDate() : null
                  const responded = approved + declined
                  const progress = total > 0 ? Math.min(100, Math.round((responded / total) * 100)) : 0

                  return (
                    <tr key={request.id}>
                      <td>
                        <div className="mediaConsentRequestName">
                          <strong>{request.title || 'Untitled request'}</strong>
                          {request.description && <span>{request.description}</span>}
                        </div>
                      </td>
                      <td className="mediaConsentDate">{createdAt ? createdAt.toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}</td>
                      <td>
                        <div className="mediaConsentResponseCounts">
                          <button type="button" className="mediaConsentCount mediaConsentCount--approved" disabled={approved === 0} onClick={() => openModal(request, 'approved')}>
                            {approved} approved
                          </button>
                          <button type="button" className="mediaConsentCount mediaConsentCount--declined" disabled={declined === 0} onClick={() => openModal(request, 'declined')}>
                            {declined} declined
                          </button>
                          <span className="mediaConsentCount mediaConsentCount--pending">{pending} pending</span>
                        </div>
                      </td>
                      <td>
                        <div className="mediaConsentProgressLabel"><span>{responded} of {total}</span><strong>{progress}%</strong></div>
                        <div className="mediaConsentProgressTrack" role="progressbar" aria-valuenow={progress} aria-valuemin="0" aria-valuemax="100" aria-label={`${progress}% responses received`}>
                          <span style={{ width: `${progress}%` }} />
                        </div>
                      </td>
                      <td>
                        <button type="button" className="mediaConsentMediaButton" onClick={() => openMediaModal(request)}>
                          <FontAwesomeIcon icon={faImages} />
                          {request.media?.length || 0} {request.media?.length === 1 ? 'file' : 'files'}
                          <FontAwesomeIcon className="mediaConsentExternalIcon" icon={faArrowUpRightFromSquare} />
                        </button>
                      </td>
                      <td><span className={`mediaConsentBadge ${getStatusClass(status)}`}>{status}</span></td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          </div>
        )}
      </section>

      <Modal show={modalShow} onHide={() => setModalShow(false)} centered size="lg" contentClassName="mediaConsentModal">
        <Modal.Header closeButton>
          <Modal.Title>
            {modalRequestTitle} - {modalStatus === 'approved' ? 'Approved' : 'Declined'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalLoading && <div className="text-center">Loading responses...</div>}
          {!modalLoading && modalStudents.length === 0 && (
            <div className="text-center">No students found.</div>
          )}
          {!modalLoading && modalStudents.length > 0 && (
            <div className="table-responsive">
              <Table bordered hover>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Phone</th>
                    <th>Responded At</th>
                  </tr>
                </thead>
                <tbody>
                  {modalStudents.map((student) => {
                    const respondedAt = student.respondedAt && student.respondedAt.toDate
                      ? student.respondedAt.toDate()
                      : null
                    return (
                      <tr key={student.id}>
                        <td>{student.studentName || '—'}</td>
                        <td>
                          <MaskedPhone
                            phoneNumber={student.phoneNumber}
                            studentId={student.studentId || student.userId}
                            studentName={student.studentName}
                          />
                        </td>
                        <td>{respondedAt ? respondedAt.toLocaleString() : '—'}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </Modal.Body>
      </Modal>

      <Modal show={mediaModalShow} onHide={() => setMediaModalShow(false)} centered size="lg" contentClassName="mediaConsentModal">
        <Modal.Header closeButton>
          <Modal.Title>Media Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {mediaModalRequest && (
            <div>
              <h5>{mediaModalRequest.title}</h5>
              <p className="text-muted">{mediaModalRequest.description}</p>
              {renderMediaItems(mediaModalRequest)}
            </div>
          )}
          {!mediaModalRequest && <div className="text-center">No media details available.</div>}
        </Modal.Body>
      </Modal>
    </div>
  )
}

export default MediaConsentComp
